import { ReactElement, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel
} from "@mui/material";
import { IAlarmData } from "./types";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { stableSort } from "../../system/utils";
import StyledTableRow from "../../components/styledTableRow";
import { IColumn, Order } from "../../types";
import TableContainer from "../../components/tableContainer";

const columns: IColumn[] = [
  { id: "id", label: "Code", minWidth: 170 },
  { id: "status", label: "Status", minWidth: 100 },
  {
    id: "date",
    label: "Date",
    minWidth: 100,
    align: "right"
  },
  {
    id: "time",
    label: "Time",
    minWidth: 100,
    align: "right",
  },
  {
    id: "description",
    label: "Description",
    minWidth: 200,
  },
];

interface IAlarmTableProps {
  data: IAlarmData[];
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }

  return 0;
}


function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const AlarmTable = ({ data }: IAlarmTableProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof IAlarmData>("id");
  // const [selected, setSelected] = useState<string[]>([]);


  const createSortHandler = (property: keyof IAlarmData) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return(
    <div>
      <TableContainer style={{ maxHeight: 582 }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead >
            <TableRow >
              {columns.map((column) =>
                <TableCell style={{ backgroundColor: "#b0bec5",  minWidth: column.minWidth  }}
                  key={column.id}
                  align={column.align}
                  sortDirection={orderBy === column.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={createSortHandler(column.id as keyof IAlarmData)}
                  >
                    {column.label}
                    {orderBy === column.id
                      ? <span className="cell-visuallyHidden">
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </span>
                      : null}
                  </TableSortLabel>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(data, getComparator(order, orderBy))
              .map((row: any, index: number) => {
                return (
                  <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`alarm-row-${index}`}>
                    {columns.map((column: IColumn) => {
                      const value = row[column.id];
                      if (column.id === "status") {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <ErrorOutlineIcon fontSize="large" style={{ color: value === 1 || value === 3 ? "#e64a19" : "#90a4ae" }} />
                            <CheckCircleOutlineIcon fontSize="large" style={{ color: value === 3 ? "#388e3c" : "#90a4ae" }}/>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format && typeof value === "number" ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};


export default AlarmTable;
